import BackButton from "../components/BackButton";
import { formatFranc } from '../lib/utils';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { BarChart3, Trophy, Users, Zap, Crown, TrendingUp, Calendar, Star, Shield, Activity } from 'lucide-react';
import FrancIcon from '../components/FrancIcon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const TYPE_COLORS = [
  '#5a2d5e', '#1e3a5f', '#c97a2e', '#2d7d5e', '#b8860b', '#c94070', '#5e4da6'
];

const TYPE_LABELS = {
  country_leader: 'Leaders', ceo: 'CEOs', actor: 'Actors',
  athlete: 'Athletes', musician: 'Artists', influencer: 'Influencers',
};

export default function UserStatsPage({ onBack }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leagues, setLeagues] = useState([]);        // {league_id, league_name, league_type}
  const [selectedLeague, setSelectedLeague] = useState('');  // '' = all leagues
  const [loading, setLoading] = useState(true);

  // 2026-06 (Task 7) — My Stats has its own league scope (independent of the
  // News/Stage/Rankings header). Default = all leagues; switching re-fetches
  // per-league so the People counter + ranks reflect that league only.
  const fetchStats = (lid) => {
    setLoading(true);
    api.get('/stats/my', { params: lid ? { league_id: lid } : {} })
      .then(res => {
        setStats(res.data);
        if (!lid && Array.isArray(res.data.league_ranks)) setLeagues(res.data.league_ranks);
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchStats(''); }, []);

  const handleLeagueChange = (v) => {
    const lid = v === 'all' ? '' : v;
    setSelectedLeague(lid);
    fetchStats(lid);
  };

  if (loading && !stats) {
    return <div className="p-4 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>;
  }

  if (!stats) {
    return <div className="p-4 text-center text-muted-foreground">Failed to load stats</div>;
  }

  // Pie chart data
  const pieData = Object.entries(stats.type_breakdown || {}).map(([type, count], i) => ({
    name: TYPE_LABELS[type] || type,
    value: count,
    color: TYPE_COLORS[i % TYPE_COLORS.length],
  }));

  return (
    <div className="p-4" data-testid="user-stats-page">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <BarChart3 className="h-5 w-5 inline mr-2" />
        {stats.username}'s Dashboard
      </h2>

      {/* Task 7 — League scope selector (independent of the main app league). */}
      {leagues.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="stats-league-selector">
          <span className="text-xs font-semibold text-muted-foreground">League:</span>
          <Select value={selectedLeague || 'all'} onValueChange={handleLeagueChange}>
            <SelectTrigger className="h-8 text-xs w-auto min-w-[160px] max-w-[280px]" data-testid="stats-league-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All leagues</SelectItem>
              {leagues.map(lr => (
                <SelectItem key={lr.league_id} value={lr.league_id} className="text-xs">{lr.league_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Trophy className="h-3 w-3" /> Total ₣ Earned</p>
          <p className="text-2xl font-bold tabular-nums">{formatFranc(stats.total_points)}</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> This Week</p>
          <p className="text-2xl font-bold tabular-nums text-[hsl(var(--positive-text))]">
            {formatFranc(stats.weekly_points, { sign: true })}
          </p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> People</p>
          <p className="text-2xl font-bold tabular-nums" data-testid="stats-people-count">{stats.total_entities}/{stats.roster_size || 18}</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><FrancIcon className="h-3 w-3" /> Budget Left</p>
          <p className="text-2xl font-bold tabular-nums">{formatFranc(stats.total_budget_remaining)}</p>
        </div>
      </div>

      {/* League Rankings */}
      {stats.league_ranks?.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" /> League Rankings
          </h3>
          <div className="space-y-2">
            {stats.league_ranks.map((lr, i) => (
              <div key={`${lr.league_id || lr.league_name}-${i}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-secondary/30">
                <div>
                  <p className="text-sm font-medium">{lr.league_name}</p>
                  <Badge variant="secondary" className="text-[9px]">{lr.league_type}</Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold tabular-nums">#{lr.rank || '?'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entity Type Breakdown */}
      {pieData.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
          <h3 className="text-sm font-semibold mb-3">Team Composition</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={120} height={120}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={2}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(val, name) => [`${val} people`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-1">
              {pieData.map((d, i) => (
                <div key={`${d.name || d.color}-${i}`} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span>{d.name}</span>
                  </div>
                  <span className="font-bold tabular-nums">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Scores */}
      {stats.recent_scores?.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4" /> Recent Scoring Events
          </h3>
          <div className="space-y-1">
            {stats.recent_scores.map((s, i) => (
              <div key={`${s.id || s.created_at || s.event_type}-${i}`} className="flex items-center justify-between px-3 py-1.5 rounded-lg border border-border/20">
                <span className="text-xs capitalize">{s.event_type?.replace(/_/g, ' ')}</span>
                <span className="text-xs font-bold tabular-nums text-[hsl(var(--positive-text))]">{formatFranc(s.total_points, { sign: true })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Account Info */}
      <div className="bg-card border border-border/60 rounded-2xl p-4">
        <h3 className="text-sm font-semibold mb-3">Account</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-medium">{stats.username}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
            <Badge variant={stats.premium ? 'default' : 'secondary'}>{stats.premium ? 'Premium' : 'Free'}</Badge>
          </div>
          <div className="flex justify-between"><span className="text-muted-foreground">Member Since</span>
            <span>{stats.member_since ? new Date(stats.member_since).toLocaleDateString() : 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
